<template>
  <v-card>
    <v-toolbar dark color="primary">
      <v-btn icon dark @click="cancel()">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ $lang("housekeeping.title_update") }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn text @click="submit()" :loading="loading">
          {{ $lang("main.crud.update") }}
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>
    <v-card-text>
      <v-container v-if="!initialized">
        <v-row>
          <v-col cols="12" xs="12" md="4">
            <v-skeleton-loader v-for="i in $range(4)" :tile="false" :boilerplate="false" type="text" class="mt-10"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" xs="12" md="8">
            <v-skeleton-loader :tile="false" :boilerplate="false" type="table" class="mt-10"></v-skeleton-loader>
          </v-col>
        </v-row>
      </v-container>
      <v-container v-if="initialized">
        <v-row>
          <v-col cols="12" xs="12" md="4">
            <v-date
              v-model="form.date"
              :label="$lang('housekeeping.fields.date')"
              :error="!!validation.date"
              :error-messages="!!validation.date ? validation.date : []"
            ></v-date>
            <v-text-field
              v-model="form.time"
              type="time"
              :label="$lang('housekeeping.fields.time')"
              :error="!!validation.time"
              :error-messages="!!validation.time ? validation.time : []"
              clearable
            ></v-text-field>
            <c-units-autocomplete
              v-model="form.unit_id"
              :label="$lang('housekeeping.fields.unit_id')"
              :error="!!validation.unit_id"
              :error-messages="!!validation.unit_id ? validation.unit_id : []"
            />
            <v-select
              v-model="form.cleaning_type"
              :label="$lang('housekeeping.fields.cleaning_type')"
              :items="$in('ConstCleaningTypes')"
              :error="!!validation.cleaning_type"
              :error-messages="!!validation.cleaning_type ? validation.cleaning_type : []"
            ></v-select>
            <c-users-autocomplete
              v-model="form.user_id"
              :label="$lang('housekeeping.fields.user')"
              :error="!!validation.user_id"
              :error-messages="!!validation.user_id ? validation.user_id : []"
              clearable
              :options="userOptions"
            />
            <v-boolean
              v-model="form.is_change_linen"
              :label="$lang('housekeeping.fields.is_change_linen')"
            />
            <v-select
              v-model="form.status"
              :label="$lang('housekeeping.fields.status')"
              :items="$in('ConstReservationTaskStatuses')"
              :error="!!validation.status"
              :error-messages="!!validation.status ? validation.status : []"
            />
            <v-textarea
              filled
              v-model="form.notes"
              :label="$lang('housekeeping.fields.notes')"
              :error="!!validation.notes"
              :error-messages="!!validation.notes ? validation.notes : []"
            ></v-textarea>
            <v-text-field
              v-if="$can('MANAGE_HOUSEKEEPING')"
              v-model="form.cost"
              :label="$lang('housekeeping.fields.cost')"
              prepend-icon="mdi-cash"
              :error="!!validation.cost"
              :error-messages="!!validation.cost ? validation.cost : []"
            ></v-text-field>
          </v-col>
          <v-col v-if="!!form.steps" cols="12" xs="12" md="8">
            <v-card outlined>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                  <tr>
                    <th class="text-left" style="width: 90%">{{ $lang('housekeeping-settings.fields.description') }}</th>
                    <th class="text-center" style="width: 10%">{{ $lang('main.crud.actions') }}</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(step, index) in form.steps" :key="index">
                    <td>
                      <v-text-field
                        v-model="step.description"
                        dense
                      ></v-text-field>
                    </td>
                    <td>
                      <div class="text-right">
                        <v-icon @click="removeStep(index)" v-if="form.steps.length > 1" color="error">mdi-minus</v-icon>
                        <v-icon @click="addStep()" v-if="index === form.steps.length - 1" color="success">mdi-plus</v-icon>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
  import { mapState } from "vuex";
  import MixpanelEvents from "@/constants/events";

  import form from "@/pages/housekeeping/mixins/form";

  import {
    GET_HOUSEKEEPING_TASKS_SINGLE,
    UPDATE_HOUSEKEEPING_TASKS,
    UPDATE_HOUSEKEEPING_TASKS_RESET,
  } from "@/store/housekeeping-tasks/actions";

  export default {
    name: "EditingForm",
    mixins: [
      form
    ],
    props: [
      'itemId'
    ],
    beforeMount() {
      this.$store.dispatch(GET_HOUSEKEEPING_TASKS_SINGLE, {
        id: this.itemId,
      });
    },
    methods: {
      resetAction() {
        return UPDATE_HOUSEKEEPING_TASKS_RESET;
      },
      submitAction() {
        return UPDATE_HOUSEKEEPING_TASKS;
      },
      submitActionOptions() {
        return {
          id: this.itemId,
          params: this.form
        };
      },
      track() {
        this.$monitorService.track(MixpanelEvents.HOUSEKEEPING_TASK_UPDATED, {
          is_user_changed: this.form.user_id != this.item.user_id,
          is_date_changed: this.form.date != this.item.date,
          is_cleaning_type_changed: this.form.cleaning_type != this.item.cleaning_type,
          is_change_linen_changed: this.form.is_change_linen != this.item.is_change_linen,
        });
      },
      populate(item) {
        if (item.steps.length) {
            item.steps.map((row, index) => {
              if (this.$isEmpty(row)) {
                item.steps[index] = {};
              }
            });
        }

        this.form = { ...this.form, ...item };
      }
    },
    computed: {
      ...mapState({
        item: state => state.housekeepingTasks.getSingleRequest.item,
        loading: state => state.housekeepingTasks.editingRequest.loading,
        validation: state => state.housekeepingTasks.editingRequest.validation,
      })
    },
  };
</script>
